import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';

// import reportWebVitals from './reportWebVitals';

import { App } from './app/App';
import { rootStore } from './app/rootStore';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from 'src/utils/msalConfig';
import {
  init,
  browserTracingIntegration,
  replayIntegration,
  browserProfilingIntegration,
} from '@sentry/react';
// import * as serviceWorker from './service-worker';

// import 'rsuite/dist/rsuite.min.css';

import './i18n/i18n';

import './index.scss';
import './sass/index.scss';
import { AppUpdateModal } from 'src/components/AppUpdateModal';
import { initializeMixpanel } from './mix-panel';
import { getCurrentUser } from './utils/getCurrentUser';
import { isAllowedIframeOrigin, getParentOrigin } from './utils/iframeSecurity';
import { IframeBlocked } from './components/IframeBlocked';
const environment = process.env.REACT_APP_ENVIRONMENT;
const version = process.env.REACT_APP_VERSION;

// Only initialize Sentry for non-local environments and when not running on localhost
if (
  environment &&
  ['production', 'qa', 'development'].includes(environment) &&
  window.location.hostname !== 'localhost'
) {
  // Define base config type with optional replay properties
  type SentryConfig = {
    dsn: string | undefined;
    environment: string;
    release: string | undefined;
    sendDefaultPii: boolean;
    integrations: any[];
    tracesSampleRate: number;
    tracePropagationTargets: RegExp[];
    replaysSessionSampleRate?: number;
    replaysOnErrorSampleRate?: number;
  };

  const config: SentryConfig = {
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment,
    release: version,
    sendDefaultPii: true,
    integrations: [browserTracingIntegration(), browserProfilingIntegration()],
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    tracePropagationTargets: [], // Initialize empty, will be set based on environment
  };

  if (environment === 'production') {
    // Production gets full monitoring with session replay
    // @ts-ignore - Known issue with Sentry types
    config.integrations = [
      browserTracingIntegration(),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ];
    // Session Replay settings only for production
    config.replaysSessionSampleRate = 0.1; // 10% of sessions
    config.replaysOnErrorSampleRate = 1.0; // 100% of sessions with errors
  }

  // Set environment-specific trace propagation targets
  if (environment === 'production') {
    config.tracePropagationTargets = [/^https:\/\/abm\.sprouts\.ai/];
  } else if (environment === 'qa') {
    config.tracePropagationTargets = [/^https:\/\/qa\.sprouts\.ai/];
  } else if (environment === 'development') {
    // Development environments might have different URLs, so include all possible dev domains
    config.tracePropagationTargets = [/^https:\/\/dev\.sprouts\.ai/];
  }

  init(config);
}
const persistor = persistStore(rootStore);
const msalInstance = new PublicClientApplication(msalConfig);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const AppContainer = () => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isIframeBlocked, setIsIframeBlocked] = useState(false);
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);

  useEffect(() => {
    // Check iframe access on mount
    const checkIframeAccess = () => {
      const allowed = isAllowedIframeOrigin();
      if (!allowed) {
        setIsIframeBlocked(true);
        setParentOrigin(getParentOrigin());
      }
    };

    checkIframeAccess();
  }, []);

  useEffect(() => {
    const setupMixPanel = async () => {
      try {
        await getCurrentUser();
        initializeMixpanel();
      } catch (error) {
        console.log('ERROR: Could not able to setup Mix-Panel', error);
      }
    };
    sessionStorage.setItem('copilot_session_id', '');
    setupMixPanel();
  }, []);

  useEffect(() => {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'showUpdateModal') {
        setShowUpdateModal(true);
      }
    });
  }, []);

  // Block rendering if iframe access is not allowed
  if (isIframeBlocked) {
    return <IframeBlocked parentOrigin={parentOrigin} />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <GoogleOAuthProvider clientId={String(process.env.REACT_APP_GOOGLE_CLIENT_ID)} nonce="nonce">
          <React.StrictMode>
            <Provider store={rootStore}>
              <PersistGate loading={null} persistor={persistor}>
                <BrowserRouter>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {showUpdateModal ? <AppUpdateModal /> : <App />}
                  </LocalizationProvider>
                </BrowserRouter>
              </PersistGate>
            </Provider>
          </React.StrictMode>
      </GoogleOAuthProvider>
    </MsalProvider>
  );
};
root.render(
  <AppContainer />,
);

// // If you want to start measuring performance in your app, pass a function
// // to log results (for example: reportWebVitals(console.log))
// // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();

// serviceWorker.unregister();
